import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import { categories, movies } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    setActiveSlug(slug || 'all');
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeSlug);

  const filteredMovies = useMemo(() => {
    if (activeSlug === 'all') return movies;
    return movies.filter(m => m.category === activeSlug);
  }, [activeSlug]);

  const title = currentCategory
    ? `${currentCategory.name} - 神马影院高清在线观看`
    : '全部影片 - 神马影院高清在线观看';

  const description = currentCategory
    ? `神马影院${currentCategory.name}频道，提供海量高清${currentCategory.name}在线观看。热播${currentCategory.name}免费播放，流畅不卡顿，即点即播。`
    : '神马影院全部分类影片，涵盖动作片、喜剧片、爱情片、科幻片、恐怖片、动画片等全类型高清电影，免费在线观看。';

  return (
    <>
      <PageMeta
        title={title}
        description={description}
        canonical={`https://www.shenmayingyuan.com/category/${activeSlug}`}
        schemaScripts={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": title,
            "description": description,
            "url": `https://www.shenmayingyuan.com/category/${activeSlug}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "神马影院",
              "url": "https://www.shenmayingyuan.com"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.shenmayingyuan.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": currentCategory ? currentCategory.name : '全部影片',
                "item": `https://www.shenmayingyuan.com/category/${activeSlug}`
              }
            ]
          }
        ]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 min-h-screen">
        <div className="w-full px-4 md:px-6 pt-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory ? currentCategory.name : '全部影片' },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
              {currentCategory ? currentCategory.name : '全部影片'}
            </h1>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 pb-12">
                {filteredMovies.map(movie => {
                  const cat = categories.find(c => c.slug === movie.category);
                  return (
                    <MovieCard
                      key={movie.id}
                      id={movie.id}
                      title={movie.title}
                      year={movie.year}
                      category={movie.category}
                      rating={movie.rating}
                      poster={movie.poster}
                      categoryName={cat?.name}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                  <i className="ri-film-line text-3xl text-foreground-400"></i>
                </span>
                <p className="text-foreground-500">暂无该分类的影片</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}